// ----------------------------------------------------------------------------
//   Imports
// ----------------------------------------------------------------------------


// React
import React, { Component, useState, useEffect, useRef, useCallback, useContext } from "react";
import { Route, NavLink, Routes, HashRouter, useParams, useNavigate } from "react-router-dom";


// Material UI
import {
    Box,
    Button,
    Card,
    CardContent,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Fab,
    Fade,
    Grow,
    IconButton,
    LinearProgress,
    Slide,
    Stack,
    Tooltip,
    Typography,
    Zoom,
} from "@mui/material";

// Icons
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CloseIcon from '@mui/icons-material/Close';
import PreviewIcon from '@mui/icons-material/Preview';
import ScreenRotationIcon from '@mui/icons-material/ScreenRotation';
import SettingsIcon from '@mui/icons-material/Settings';
import SmartDisplayIcon from '@mui/icons-material/SmartDisplay';

// Custom Components: Basic
import AligningBox from "../components/utils/AligningBox.js";
import LoadingIcon from "../components/loading/LoadingIcon.js";
import { RenderIf, RenderIfAdmin } from "../components/utils/RenderIf.js";
import ScrollCard from "../components/ScrollCard.js";
import { ActionButtonFullwidth, ActionButtonOverlay } from "../components/utils/ActionButtons.js";
import { UserContext } from "../Contexts.js";

// Custom Components: Instructions
import { VideoInstructions } from "../components/video_page/VideoInstructions.js";

// Custom Components: Webcam
import { WebcamVideo } from "../components/video_page/WebcamVideo";
import { WebcamSettingsCard } from "../components/video_page/WebcamSettings.js";

// Custom Components: Practice Video
import { PracticeVideoCard } from "../components/video_page/PracticeVideo.js";

// Custom Components: Recordings
import { SingleRecordingModal } from "../components/video_page/SingleRecordingCard.js";
import { uploadRecording, uploadRecordingsBatch, spoofUploadRecording } from "../scripts/uploading.js";
import { retrieveNonUploadedRecordings } from "../scripts/backupRecordings.js";

// Custom Scripts: API
import { createActivity } from '../api/activity-service';
import { fetchVideo } from '../api/course-service.js';

// Custom Scripts: Local
import { ErrorState, StateError } from "../scripts/errorHandling.js";
import { exitFullScreen } from "../scripts/fullscreen.js";
import { LoadedDataState, useLoadedData } from "../scripts/loadedData/loadedData";
import { useWebcamObject, WebcamState } from "../scripts/video/webcam.js";
import { useMediaRecorder } from "../scripts/video/utils.js";
import { useVideoManager, VideoManagerEvents } from '../scripts/video/videoManager.js';
import { sleep, useScreenIsShort, useScreenIsCompact, useScreenIsTiny } from "../scripts/utils.js";

// Custom Scripts: Shared (API + client)
import { activityTypes } from "@fontan/shared";



// ----------------------------------------------------------------------------
//   Helper Function(s)
// ----------------------------------------------------------------------------


/**
 * Log an activity on the video.
 * TODO: Is more involved error handling required?
 * TODO: Does this adequately cover all events?
 *
 * @param {activityTypes} type
 *   The type of activity to be logged.
 *
 * @param {video} video
 *   An object representing the current video. Expected to include at least a `name` field.
 */
async function createVideoActivity(type, video, metadata) {

  // TODO: Configure this elsewhere
  const verbose = false;

  // Optionally log event
  if (verbose) console.debug("Creating:", type, video, metadata);

  // Create the activity
  try {
    await createActivity(type, video.name, metadata);
  }

  // Catch and optionally log errors
  catch(err) {
    if (verbose) console.error(err);
  }
}


/**
 * Titles of each step in the process.
 * Used to match instructions wording with buttons.
 */
const StepTitles = [
  "Start Camera",
  "Practice and Record",
  "Upload Video",
]



// ----------------------------------------------------------------------------
//   Helper Component(s)
// ----------------------------------------------------------------------------


/**
 * Render the title of the video, along with a return button to the homepage.
 */
function VideoTitle({ title, onClose }) {

  // Smaller title on short screens, where the sidebar is narrow too
  const screenIsCompact = useScreenIsCompact();
  const screenIsTiny    = useScreenIsTiny();

  return (
    <Stack direction="row" gap={1} alignItems="center">

      {/* Title */}
      <Typography
        variant="h2" fontSize={ screenIsTiny ? "14pt" : screenIsCompact ? "16pt" : "20pt" } fontWeight="bold" lineHeight={1}
        flexGrow={1} alignSelf="center" paddingRight={1}
      >
        { title }
      </Typography>

      {/* X Button */}
      <IconButton
        variant = "outlined"
        order={{xs: 1, sm: "unset"}}
        onClick = { onClose }
      >
        <CloseIcon />
      </IconButton>

    </Stack>
  )
}


/**
 * Render the title of the video as a card.
 */
function VideoTitleCard({ title, onClose }) {
  return (
    <Card>
      <CardContent sx={{ 'paddingBottom': '16px !important', paddingLeft: 3 }}>
        <VideoTitle title={ title } onClose={ onClose } />
      </CardContent>
    </Card>
  )
}


/**
 * Render the instructions as a card.
 */
function VideoInstructionsCard({ steps, currentStep }) {

  // Tighter padding on short screens
  const screenIsCompact = useScreenIsCompact();

  // The card grows to fit the steps (it never clips or scrolls them), and fills any extra
  // height its slot reserves, with the steps centered.
  return (
    <Card sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
      <CardContent sx={{
        flexGrow: 1,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        ...(screenIsCompact && { paddingY: "8px !important" }),
      }}>
        <VideoInstructions steps={ steps } currentStep={ currentStep } />
      </CardContent>
    </Card>
  )
}


/**
 * Render the video title and instructions together on a single card.
 */
function VideoTitleAndInstructionsCard({ title, onClose, steps, currentStep }) {
  return (
    <ScrollCard title={ <VideoTitle title={ title } onClose={ onClose } /> }>
      <VideoInstructions steps={ steps } currentStep={ currentStep } />
    </ScrollCard>
  )
}



// ----------------------------------------------------------------------------
//   Helper Component(s): Action Button
// ----------------------------------------------------------------------------


/**
 * Action buttons in a popup modal.
 */
function ModalActionButton({ onClick, color, disabled, children }) {
  return (
    <Button
      variant  = "contained"
      onClick  = { onClick }
      color    = { color }
      disabled = { disabled }
      sx = {{
        flexGrow: 1,
        padding: 2,
        margin: "0 !important",
        borderRadius: 0,
      }}
    >
      { children }
    </Button>
  )
}



// ----------------------------------------------------------------------------
//   Helper Component(s): Popup Modals
// ----------------------------------------------------------------------------


function RotateDeviceOverlay({ isPortrait }) {
  return (
    <Fade in={isPortrait}>
      <Box
        sx={{
          display: isPortrait ? 'flex' : 'none',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'white',
          zIndex: 10000,
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 3,
        }}
      >
        <ScreenRotationIcon sx={{ fontSize: '120px', color: '#4447ad' }} />
        <Typography variant="h4" textAlign="center" px={4}>
          Please Rotate Your Device
        </Typography>
        <Typography variant="h6" textAlign="center" px={4} color="text.secondary">
          This page works best in landscape orientation
        </Typography>
      </Box>
    </Fade>
  );
}


function UploadingModal({ open, status, progress, onClose }) {

  // How long to wait before unloading the "uploading" content
  const transitionDelayMS = 600;

  // Duration of the transition itself
  const transitionLengthMS = 200;

  // Internal states
  // Used to coordinate animations in response to upload status
  const InternalState = {
    uploading:   'uploading',
    fadeSuccess: 'fadeSuccess',
    success:     'success',
    fadeFailure: 'fadeFailure',
    failure:     'failure',
  }

  // Track the internal (animation) state
  const [displayState, setDisplayState] = useState( InternalState.uploading )

  // Sync animation states to upload state
  useEffect(() => {

    // On success, fade out the uploading elements, then fade in the success screen
    if (status.state === LoadedDataState.SUCCESS) {
      const timer1 = setTimeout(() => setDisplayState(InternalState.fadeSuccess), transitionDelayMS);
      const timer2 = setTimeout(() => setDisplayState(InternalState.success), transitionDelayMS + transitionLengthMS);
      return () => {clearTimeout(timer1); clearTimeout(timer2)};
    }

    // On error, fade out the uploading text, then fade in the error text
    else if (status.state === LoadedDataState.FAILURE) {
      console.error(status.error)
      const timer1 = setTimeout(() => setDisplayState(InternalState.fadeFailure), transitionDelayMS);
      const timer2 = setTimeout(() => setDisplayState(InternalState.failure), transitionDelayMS + transitionLengthMS);
      return () => {clearTimeout(timer1); clearTimeout(timer2)};
    }

    // Otherwise, reset internal state
    else if (status.state === LoadedDataState.WORKING || status.state === LoadedDataState.NONE) {
      setDisplayState(InternalState.uploading);
    }

  }, [ status.state, transitionDelayMS ]);


  const navigate = useNavigate();

  // Track the width of the content -- required for horizontal collapse of loading bar
  const [contentWidth, setContentWidth] = useState(0);
  const contentRef = useCallback((node) => {
    if (node !== null) {
      setContentWidth(node.getBoundingClientRect().width);
    }
  }, []);

  // Track whether to show error details
  const [ showErrorDetails, setShowErrorDetails ] = useState(false);

  /**
   * Return to the homepage.
   */
  function handleGoHome() {
    return navigate('/student');
  }

  return (
    <Dialog
      open={ open }
      maxWidth="650px"
      sx={{ 'textAlign': 'center', transition: 'height' }}
      onClose={onClose}
    >
      <Stack direction="column" padding="24px">
        <DialogTitle variant="h4" component="h3" sx={{ paddingTop: 0, borderBottom: "1px solid rgba(0, 0, 0, 0.12)" }}>
          {
            {
              [ LoadedDataState.NONE    ]: "Uploading...",
              [ LoadedDataState.WORKING ]: "Uploading...",
              [ LoadedDataState.SUCCESS ]: "Upload Complete!",
              [ LoadedDataState.FAILURE ]: "Upload Failed",
            }[ status.state ]
          }
        </DialogTitle>

        <DialogContent ref={contentRef} sx={{ paddingTop: "24px !important" }}>

          {/* Loading text */}
          <Fade in={ displayState === InternalState.uploading } timeout={ transitionLengthMS } mountOnEnter unmountOnExit>
            <div>
              <DialogContentText variant="h6" color="black">
                Please wait while we upload your recording.
              </DialogContentText>
              <DialogContentText variant="h6" color="black">
                If you leave this page, your progress will <b>NOT</b> be saved.
              </DialogContentText>
            </div>
          </Fade>

          {/* Success text */}
          <Fade in={ displayState === InternalState.success } timeout={ transitionLengthMS } mountOnEnter unmountOnExit>
            <div>
              <DialogContentText variant="h6" color="black">
                Your recording has been uploaded successfully!
              </DialogContentText>
              <DialogContentText variant="h6" color="black">
                You may now leave this page.
              </DialogContentText>
            </div>
          </Fade>

          {/* Failure text */}
          <Fade in={ displayState === InternalState.failure } timeout={ transitionLengthMS } mountOnEnter unmountOnExit>
            <div>
              <DialogContentText variant="h6" color="black">
                There was a problem uploading your recording.
              </DialogContentText>
            </div>
          </Fade>
        </DialogContent>

        {/* Loading bar */}
        <Collapse
          in = { [ InternalState.uploading, InternalState.fadeFailure, InternalState.failure ].includes( displayState ) }
          timeout = {{ enter: 0, exit: transitionLengthMS }}
          // style   = {{ transitionDelay: [ InternalState.uploading, InternalState.fadeFailure, InternalState.failure ].includes( displayState ) ? 0 : `${transitionLengthMS}ms` }}
        >
          <AligningBox position="relative" width="100%">
            <Collapse
              orientation="horizontal"
              in = { [ InternalState.uploading, InternalState.fadeFailure, InternalState.failure ].includes( displayState ) }
              timeout={{ enter: 0, exit: transitionLengthMS }}
            >
              <Box position="relative" width={ contentWidth } height="100%">
                <LinearProgress
                  variant = "determinate"
                  value   = { progress }
                  sx = {{
                    height:  "48px",
                    borderRadius: "8px",
                    marginY: 2,
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: status.state === LoadedDataState.SUCCESS ? "#4caf50" : status.state === LoadedDataState.FAILURE ? "#DC143C" : "#b39fe7",
                    },
                    "&.MuiLinearProgress-root": {
                      backgroundColor: status.state === LoadedDataState.SUCCESS ? "#2d662f" : status.state === LoadedDataState.FAILURE ? "#C41E3A" : "#4447ad",
                    },
                  }}
                />
              </Box>
            </Collapse>
          </AligningBox>
        </Collapse>

        <RenderIf condition={status.error && (typeof status.error === 'object' && status.error !== null && status?.error?.cause)}>
          <Box mt={2}>

            <Box display="flex" justifyContent="flex-end" alignItems="center" pr={2}>
              <Button
                variant="text"
                size="small"
                onClick={() => setShowErrorDetails(v => !v)}
                sx={{
                  textTransform: 'none',
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  minWidth: 0,
                  px: 1,
                  py: 0,
                  color: 'grey',
                }}
              >
                {showErrorDetails ? 'Hide Full Error Details' : 'Show Full Error Details'}
              </Button>
            </Box>

            <Collapse in={ showErrorDetails }>
              <Box
                component="pre"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.95em',
                  background: '#f7f7f7',
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  p: 2,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  mt: 1,
                }}
              >
                { String(status?.error?.cause) }
              </Box>
            </Collapse>
          </Box>
        </RenderIf>
      </Stack>

      <Collapse
        in={ displayState === InternalState.success }
        timeout={{ enter: transitionLengthMS }}
        unmountOnExit
      >
        <DialogActions sx={{"padding": 0, "gap": 0, minHeight: 64, overflow: "visible", flexDirection: "column", alignItems: "stretch", transition: 'min-height 400ms cubic-bezier(0.4,0,0.2,1)' }}>
          <ModalActionButton
            color    = "success"
            onClick  = { handleGoHome }
            disabled = { status.state !== LoadedDataState.SUCCESS }
          >
            <Zoom in={ displayState === InternalState.success } timeout={{ enter: 200 }} style={{ transitionDelay: `${200}ms` }}>
              <Typography>
                { status.state === LoadedDataState.SUCCESS ? "Return Home" : "Please Wait..." }
              </Typography>
            </Zoom>
          </ModalActionButton>
        </DialogActions>
      </Collapse>

    </Dialog>
  );
}


function LeavePageConfirmation( props ) {

  const navigate = useNavigate();

  /**
   * Return to the homepage.
   */
  function handleGoHome() {
    return navigate('/student');
  }

  // Component Layout
  return (
    <Dialog
      open={ props.open }
      onClose={ props.onClose }
      maxWidth="650px"
      sx={{ 'textAlign': 'center' }}
    >
      <Stack direction="column" padding="16px">
        <DialogTitle variant="h4" component="h3">
          Are you sure you want to leave this page?
        </DialogTitle>

        <DialogContent>
          <DialogContentText variant="h6" color="black">
            If you leave this page, your progress will <b>NOT</b> be saved.
          </DialogContentText>
        </DialogContent>
      </Stack>

      <DialogActions sx={{"padding": 0, "gap": 0}}>
        <ModalActionButton color="error" onClick={ handleGoHome }>
          Go Back Home
        </ModalActionButton>
        <ModalActionButton color="success" onClick={ props.onClose }>
          Stay on this Page
        </ModalActionButton>
      </DialogActions>

    </Dialog>
  )
}


function SettingsFab({ content, onClick, visible }) {
  return (
    <Tooltip title={ content } arrow placement="right" slots={{ transition: Zoom }}>
      <Zoom in={visible} unmountOnExit>
        <Fab
          color      = "info"
          aria-label = { content }
          onClick    = { onClick }
          sx = {{
            backgroundColor: "#4447ad",
            '&:hover': {
              backgroundColor: "#6863d6",
            },
          }}
        >
          <SettingsIcon />
        </Fab>
      </Zoom>
    </Tooltip>
  )
}



// ----------------------------------------------------------------------------
//   Main Component
// ----------------------------------------------------------------------------


function VideoPageV2({ autoplay = true }) {

  // Gaps between cards: tighter on short screens, where every pixel of height counts
  const screenIsCompact = useScreenIsCompact();
  const screenIsTiny    = useScreenIsTiny();
  const spacing = screenIsTiny ? "12px" : screenIsCompact ? "24px" : "48px";

  // Temporary variable to control layout options
  const titleAndInstructionsTogether = false;

  // Router parameters
  const params = useParams()

  // Get user (to check role)
  const { userData } = useContext( UserContext );
  const allowAdminFeatures = userData?.value?.role === 'admin';

  // Track the current step in the process
  const [ currentStep, setCurrentStep ] = useState(1);

  // Track device orientation
  const [ isPortrait, setIsPortrait ] = useState(
    window.matchMedia("(orientation: portrait)").matches
  );

  // Listen for orientation changes
  useEffect(() => {
    const mediaQuery = window.matchMedia("(orientation: portrait)");

    const handleOrientationChange = (e) => {
      setIsPortrait(e.matches);
    };

    mediaQuery.addEventListener('change', handleOrientationChange);
    return () => mediaQuery.removeEventListener('change', handleOrientationChange);
  }, []);

  // Check the height of the screen, for responsive layout
  const screenIsShort = useScreenIsShort();

  // Modal(s)
  const [ leavingDialogOpen,   setLeavingDialogOpen   ] = useState( false );
  const [ uploadingDialogOpen, setUploadingDialogOpen ] = useState( false );
  const [ reviewDialogOpen,    setReviewDialogOpen    ] = useState( false );

  // Use ref for video control play button
  // TODO: Not the best solution, but works for now
  const playRef = useRef(null);

  // Load the current video
  const [ video, manageVideo ] = useLoadedData();
  useEffect(() => {
    manageVideo.setWorking();
    fetchVideo( params.id ).then(
      video => manageVideo.setSuccess( video ),
      error => manageVideo.setFailure( ErrorState.NO_VIDEO_DATA, error ),
    );
  }, [])

  // Create a ref to track the video object
  const videoRef = useRef(null);
  useEffect(() => { videoRef.current = video }, [video]);

  const renderIfStep = (step, component) => {
    return step === currentStep && component;
  }

  // Create an object to control the webcam configuration
  const webcamRef = useRef(null);
  const webcamObject = useWebcamObject({ ref: webcamRef });

  // Control practice video state & playback
  const videoManager = useVideoManager(
    null,
    {
      eventLogger: handleVideoManagerEvent,
      verbose:     allowAdminFeatures,
    }
  );

  // Create a custom media recorder object
  // The field `result` will store the resulting recording on completion
  const mediaRecorder = useMediaRecorder({ videoRef: videoManager.state.current.videoRef, webcamRef })

  // Track the recording upload status
  const [ uploadProgress, setUploadProgress  ] = useState( 0 );
  const [ uploadStatus,   manageUploadStatus ] = useLoadedData();

  const [ showWebcamSettings, setShowWebcamSettings ] = useState( false );


  // Track whether each step is complete,
  // i.e. whether the user should be able to move to the next step
  const step1Complete = webcamObject.getState() === WebcamState.SUCCESS || webcamObject.getState() === WebcamState.FAILURE;
  const step2Complete = videoManager.state.current.isCompleted;
  const step3Complete = uploadStatus.state === LoadedDataState.SUCCESS;

  /**
   * List of state booleans tracking whether each step is complete.
   *
   * Index `n` in the list represents step `n+1`,
   * i.e. `stepsComplete[0]` tracks whether step 1 is complete.
   */
  const stepsComplete = [
    step1Complete, step2Complete, step3Complete,
  ]
  const lastStepCompleted = stepsComplete.lastIndexOf(true) + 1


  // --------------------------------------------------------------------------
  //   Callbacks
  // --------------------------------------------------------------------------

  /**
   * When webcam is confirmed, move to step 2.
   */
  function onWebcamConfirm() {
    setCurrentStep(2);
  }

  /**
   * When the video starts playing, start recording as well.
   * Note that manual (user-initiated) play events are tracked using `handleVideoManagerEvent`.
   */
  function startRecording() {

    // Make sure video isn't already at end
    if (videoManager.state.current.progressPercent >= 100) return;

    // Start the recording
    try {
      mediaRecorder.start({ useWebcam: webcamObject.getState !== WebcamState.FAILURE });
    }
    catch (e) {
      // TODO: Enter an error state
      console.error(e)
    }
  }

  /**
   * When the video is paused, pause the recording as well.
   * Note that manual (user-initiated) pause events are tracked using `handleVideoManagerEvent`.
   */
  function pauseRecording() {
    mediaRecorder.pause();
  }

  /**
   * When the user finishes the video, complete the recording.
   */
  function finishRecording() {
    mediaRecorder.finish();
    createVideoActivity(activityTypes.USER_COMPLETED_VIDEO, videoRef.current.value)
  }

  /**
   * Handle events emitted by the video manager object.
   */
  function handleVideoManagerEvent( event, metadata ) {

    // Map video manager events to activity types tracked in the back-end
    const eventMap = {
      [ VideoManagerEvents.MANUAL_PLAY  ]: activityTypes.USER_START_RECORDING,
      [ VideoManagerEvents.MANUAL_PAUSE ]: activityTypes.USER_PAUSED_RECORDING,
    };

    // Track the activity, if applicable
    if (eventMap[event]) {
      createVideoActivity( eventMap[event], videoRef.current.value, metadata );
    }
    else {
      console.debug(`Received unhandled video manager event: ${ event }`);
    }
  }

  /**
   * Show a review modal for the recording. Admins only.
   */
  async function reviewFinalRecording() {
    if (!allowAdminFeatures) return;
    setReviewDialogOpen( true );
  }

  /**
   * Upload the recorded video
   */
  async function uploadFinalRecording() {

    // Show the uploading dialog
    exitFullScreen().catch((e) => {});
    setUploadingDialogOpen( true );

    // Initialize the upload state
    setUploadProgress(0)
    manageUploadStatus.setWorking();

    // Track the user's click to upload their recording
    createVideoActivity( activityTypes.USER_CLICK_UPLOAD, videoRef.current.value )

    // Safety check: make sure the media recorder has resolved before attempting to get the result
    await mediaRecorder.waitForResult();

    // If recording produced an error, propagate it via the UPLOAD_FAILED error state
    if (mediaRecorder.result.state === LoadedDataState.FAILURE) {
      manageUploadStatus.setFailure( ErrorState.UPLOAD_FAILED, mediaRecorder.result?.error );
      return;
    }

    // If recording has not resolved (somehow), propagate it via the UPLOAD_FAILED error state
    // NOTE: This should be impossible, but better safe than sorry
    if (mediaRecorder.result.state === LoadedDataState.WORKING || mediaRecorder.result.state === LoadedDataState.NONE) {
      manageUploadStatus.setFailure( ErrorState.UPLOAD_FAILED, `No recording found. (State: ${mediaRecorder.result.state})` );
      return;
    }

    // Retrieve any other recordings that haven't been uploaded yet (uses default function limit)
    let recordingsToUpload = await retrieveNonUploadedRecordings()

    // Make sure the new recording from this session is in the list to upload
    if (
          mediaRecorder.result.state === LoadedDataState.SUCCESS
      &&  mediaRecorder.result.value !== null
      &&  mediaRecorder.result.value.recordingID != null
      &&  !recordingsToUpload.some((recording) => recording?.recordingID === mediaRecorder.result.value.recordingID)
    ) {
      recordingsToUpload.unshift( mediaRecorder.result.value )
    }

    // Filter out any recordings that do not have a valid recordingID
    recordingsToUpload = recordingsToUpload.filter(
      (recording) => recording && recording.recordingID != null
    )

    // If there are no recordings to upload, finish here with success
    if (!recordingsToUpload.length) {
      createVideoActivity( activityTypes.VIDEO_UPLOAD_COMPLETE, videoRef.current.value, { 'recordings': 'no recordings to upload' } )
      setUploadProgress( 100 );
      manageUploadStatus.setSuccess( null );
      return;
    }

    // Pick explicit fields to track, so the blob (and any future fields) stay out of the activity log
    const recordingsMetadata = recordingsToUpload.map(recording => ({
      recordingID: recording?.recordingID,
      title:       recording?.title,
      filename:    recording?.filename,
      fileSize:    recording?.blob?.size,
      uploaded:    recording?.uploaded,
    }));

    // Track the recordings
    createVideoActivity(
      activityTypes.VIDEO_UPLOAD_START,
      videoRef.current.value,
      { 'recordings': recordingsMetadata },
    );

    // Upload the recording blob, tracking progress
    try {
      const response = await uploadRecordingsBatch(
        recordingsToUpload,
        (loaded, total) => {
          setUploadProgress( loaded / total * 100 );
        },

        // Verbose flag for admins
        allowAdminFeatures,
      )

      // Mark the upload as finished
      manageUploadStatus.setSuccess( response );

      // Track success
      createVideoActivity(
        activityTypes.VIDEO_UPLOAD_COMPLETE,
        videoRef.current.value,
        { 'recordings': recordingsMetadata },
      );
    }

    // Catch & track error state
    catch (err) {

      // Track the failure
      createVideoActivity(
        activityTypes.VIDEO_UPLOAD_FAILED,
        videoRef.current.value,
        {
          'recordings': recordingsMetadata,
          'reason':     err?.message,
          'errorName':  err?.name,

          // Per-recording failure reasons, if this was a batch upload error
          'failures':   err?.failedUploads?.slice(0, 5).map(({ recordingId, error }) => ({
            recordingId,
            errorName:    error?.name,
            errorMessage: error?.message,
            causeMessage: error?.cause?.message,
          })),
        },
      );

      const stateErr = new StateError(ErrorState.UPLOAD_FAILED, err)
      manageUploadStatus.setFailure( ErrorState.UPLOAD_FAILED, stateErr )
    }
  }


  // --------------------------------------------------------------------------
  //   Effects
  // --------------------------------------------------------------------------

  /**
   * Register event listeners on the video object
   */
  useEffect(() => {
    videoManager.setEventListener('play',  startRecording)
    videoManager.setEventListener('pause', pauseRecording)
    videoManager.setEventListener('ended', finishRecording)
  }, []);

  /**
   * Start the video playing once camera is confirmed.
   * Waits 3 seconds for the video to stop buffering, then stops.
   */
  useEffect(() => {

    // Preconditions
    if (!(currentStep === 2 && autoplay)) return;

    // Wait for video to finish buffering before starting countdown
    const maxWaitTime = 3000; // 3 seconds
    const pollInterval = 100; // Check every 100ms
    let elapsedTime = 0;

    // Start the interval
    const checkBuffering = setInterval(() => {
      elapsedTime += pollInterval;

      // If buffering is complete, start the countdown
      if (!videoManager.state.current.isBuffering) {
        clearInterval(checkBuffering);
        playRef.current?.startCountdown();
      }

      // If we've waited too long, give up
      else if (elapsedTime >= maxWaitTime) {
        clearInterval(checkBuffering);
      }
    }, pollInterval);

    // Cleanup function
    return () => clearInterval(checkBuffering);
  }, [currentStep])

  /**
   * If the upload triggers an error state, record it as an activity
   */
  useEffect(() => {
    if ( uploadStatus.state === LoadedDataState.FAILURE ) {

      // Error objects serialize as `{}`, so send their fields explicitly
      const error = uploadStatus.error;
      createVideoActivity(
        activityTypes.ENTERED_ERROR_STATE,
        videoRef.current.value,
        {
          state:        uploadStatus.state,
          errorState:   error?.state,
          errorName:    error?.name,
          errorMessage: error?.message ?? (error === undefined ? undefined : String(error)),
        },
      );
    }
  }, [ uploadStatus.state, uploadStatus.error ])


  // --------------------------------------------------------------------------
  //   Special Cases
  // --------------------------------------------------------------------------

  // State Check: Loading
  // If video data is still loading, show a loading screen
  if ( video.state === LoadedDataState.WORKING || video.state === LoadedDataState.NONE ) {
    return (
      <LoadingIcon />
    )
  }


  // --------------------------------------------------------------------------
  //   Main Layout
  // --------------------------------------------------------------------------

  return (
    <Stack direction="row" width="100%" height="100%" gap={spacing}>

        {/* Rotate Device Overlay */}
        <RotateDeviceOverlay isPortrait={isPortrait} />

        {/* Left Column */}
        <Stack justifyContent="space-between" width="30%" gap={spacing} height="100%" minHeight="0">

          {/* Title & Instructions Card (Together) */}
          <RenderIf condition={titleAndInstructionsTogether}>
            <Box height="60%">
              <VideoTitleAndInstructionsCard
                title   = { video.value?.name ? `Video ${video.value.name?.toUpperCase()}` : '' }
                onClose = { () => setLeavingDialogOpen(true) }
                steps         = { StepTitles }
                currentStep   = { lastStepCompleted }
              />
            </Box>
          </RenderIf>

          {/* Title & Instructions Cards (Separate) */}
          <RenderIf condition={!titleAndInstructionsTogether}>
            <Box>
              <VideoTitleCard
                title   = { video.value?.name ? `Video ${video.value.name?.toUpperCase()}` : '' }
                onClose = { () => setLeavingDialogOpen(true) }
              />
            </Box>
            {/* Always tall enough for the steps, so their text shows in full; on taller screens it
                also keeps its usual share of the column. The webcam area below absorbs the rest. */}
            <Box
              display       = "flex"
              flexDirection = "column"
              flexShrink    = { 0 }
              minHeight     = { screenIsCompact ? 0 : screenIsShort ? "35%" : "30%" }
            >
              <VideoInstructionsCard
                steps         = { StepTitles }
                currentStep   = { lastStepCompleted }
              />
            </Box>
          </RenderIf>

          {/* Secondary Content */}
          <Box flexGrow={1} minHeight="0" position="relative">
            {
              renderIfStep(1, (
                <Box height="100%">
                  <Slide in={showWebcamSettings} direction="right" mountOnEnter unmountOnExit>
                    <Box height="100%">
                      <WebcamSettingsCard webcam={ webcamObject } />
                    </Box>
                  </Slide>

                  <RenderIf condition={!showWebcamSettings}>
                    <AligningBox top={0} left={0} alignX="left" alignY="bottom">
                      <SettingsFab
                        onClick = { () => setShowWebcamSettings(true) }
                        content = "Webcam Settings"
                        visible = { webcamObject.getState() === WebcamState.SUCCESS && !showWebcamSettings }
                      />
                    </AligningBox>
                  </RenderIf>
                </Box>
              ))
            }
            {
              renderIfStep(2, (
                <WebcamVideo
                  webcam       = { webcamObject }
                  isRecording  = { mediaRecorder.isRecording }
                  showOptionsBar
                  showRecordingIcon
                  showSettingsIcon = {false}
                  showErrMsg   = { false }
                />
              ))
            }
          </Box>
        </Stack>
        {/* /Left Column */}

        {/* Right Column */}
        <Stack direction="column" flexGrow={1} justifyContent="space-between" width="100%" height="100%" minHeight="0" gap={spacing}>

          {/* Primary Content */}
          <Box flexGrow={1} minHeight="0">
          {
            renderIfStep(1, (
              <WebcamVideo
                webcam      = { webcamObject }
                startText   = { StepTitles[0] }
                showGuides
              />
            ))
          }
          {
            renderIfStep(2, (
              <PracticeVideoCard
                videoContent = { video }
                videoState   = { videoManager.state.current }
                videoManager = { videoManager }
                isRecording  = { mediaRecorder.isRecording }
                playRef      = { playRef }
                isComplete   = { step2Complete }
                finishButton = {
                  <Stack direction="column" alignItems="center" gap={3}>
                    <ActionButtonOverlay
                      content = { StepTitles[2] }
                      visible = { step2Complete }
                      onClick = { uploadFinalRecording }
                      endIcon = {<AssignmentTurnedInIcon sx={{ fontSize: "2em !important", paddingBottom: "2px", paddingLeft: 1 }} />}
                    />
                    <RenderIfAdmin>
                      <ActionButtonOverlay
                        content = { "Review Recording" }
                        visible = { step2Complete }
                        onClick = { reviewFinalRecording }
                        endIcon = {<PreviewIcon sx={{ fontSize: "2em !important", paddingBottom: "2px", paddingLeft: 1 }} />}
                      />
                    </RenderIfAdmin>
                  </Stack>
                }
              />
            ))
          }
          </Box>

          {/* Main Action Button */}
          {
            renderIfStep(1, (
              <ActionButtonFullwidth
                content  = { StepTitles[1] }
                onClick  = { onWebcamConfirm }
                disabled = { !step1Complete }
                endIcon  = { <SmartDisplayIcon sx={{ fontSize: "2em !important", paddingBottom: "2px", paddingLeft: 1 }} /> }
              />
            ))
          }
          {/* /Main Action Button */}

        </Stack>
        {/* /Right Column */}

      {/* Upload Progress Modal */}
      <UploadingModal
        open     = { uploadingDialogOpen }
        status   = { uploadStatus }
        progress = { uploadProgress }
        onClose  = { allowAdminFeatures ? () => setUploadingDialogOpen(false) : undefined }
      />

      {/* Leave Page Confirmation Modal */}
      <LeavePageConfirmation open={leavingDialogOpen} onClose={ () => setLeavingDialogOpen(false) } />

      <RenderIfAdmin>
        <SingleRecordingModal
          open      = { reviewDialogOpen }
          onClose   = { () => setReviewDialogOpen(false) }
          recording = { mediaRecorder?.result }
        />
      </RenderIfAdmin>

    </Stack>
  )
}


export default VideoPageV2;
